import { useGetInvoiceById } from "@entities/accounting/lib/hooks/useGetInvoiceById";
import { BillingSummaryTable } from "@features/AddBrandAccounting/ui/BillingSummaryTable";
import { ToastService } from "@shared/lib/services";
import { Money } from "@shared/lib/services/Money.service";
import { IBrandAccounting } from "@shared/lib/types/accounting";
import { LEDGER_TYPE } from "@shared/lib/types/briefLedger";
import { Spin } from "antd";

interface TransactionDetailsProps {
  transaction: IBrandAccounting;
}

export function TransactionDetails({ transaction }: TransactionDetailsProps) {
  const { invoice, isLoading } = useGetInvoiceById(
    transaction?._id || "",
    true
  );

  const purchases = invoice?.brandLedgers;

  if (isLoading) {
    return (
      <div
        style={{ display: "flex", justifyContent: "center", padding: "20px" }}
      >
        <Spin />
      </div>
    );
  }

  if (!purchases) {
    return <div>No details found.</div>;
  }

  const totalPurchaseCost = purchases
    .reduce(
      (acc, curr) =>
        curr.type === LEDGER_TYPE.Credit
          ? acc.subtract(new Money(curr.baseCost))
          : acc.add(new Money(curr.baseCost)),
      new Money(0)
    )
    .toNumber();

  const totalSellingCost = purchases
    .reduce(
      (acc, curr) =>
        curr.type === LEDGER_TYPE.Credit
          ? acc.subtract(new Money(curr.sellingCost))
          : acc.add(new Money(curr.sellingCost)),
      new Money(0)
    )
    .toNumber();

  const totalServiceCost = purchases
    .reduce(
      (acc, curr) =>
        curr.type === LEDGER_TYPE.Credit
          ? acc.subtract(new Money(curr.serviceCost))
          : acc.add(new Money(curr.serviceCost)),
      new Money(0)
    )
    .toNumber();

  const totalGst = purchases
    .reduce(
      (acc, curr) =>
        curr.type === LEDGER_TYPE.Credit
          ? acc.subtract(new Money(curr.gstAmount))
          : acc.add(new Money(curr.gstAmount)),
      new Money(0)
    )
    .toNumber();

  const grandTotal = purchases
    .reduce(
      (acc, curr) =>
        curr.type === LEDGER_TYPE.Credit
          ? acc.subtract(new Money(curr.totalAmount))
          : acc.add(new Money(curr.totalAmount)),
      new Money(0)
    )
    .toNumber();

  const handleCopySummary = () => {
    const selectedPurchasesData = purchases;
    let tableText =
      "Creator Name\tCampaign\tPurchase Cost\tService Cost\tGST\tTotal\n";
    selectedPurchasesData.forEach((p) => {
      const isNegative = p.type === LEDGER_TYPE.Credit;
      tableText += `${p.socialMediaName || p?.category || ""}\t${p.brief?.name || ""}\t₹${isNegative ? -p.baseCost : p.baseCost}\t₹${isNegative ? -p.serviceCost : p.serviceCost}\t₹${isNegative ? -p.gstAmount : p.gstAmount}\t₹${isNegative ? -p.totalAmount : p.totalAmount}\n`;
    });
    const summary = `
    Brand: ${transaction.brand?.name}
    Type: ${transaction?.type}
    
    ${tableText}
    --------------------------------------------------
    Summary Totals:
    Base Amount: ₹${totalSellingCost.toLocaleString()}
    Service Cost : ₹${totalServiceCost.toLocaleString()}
    GST: ₹${transaction.gstAmount.toLocaleString()}
    Grand Total: ₹${transaction.totalAmount.toLocaleString()}
        `.trim();
    navigator.clipboard.writeText(summary);
    ToastService.success("Summary table and details copied to clipboard!");
  };

  return (
    <BillingSummaryTable
      purchases={purchases}
      totalPurchaseCost={totalPurchaseCost}
      totalSellingCost={totalSellingCost}
      totalServiceCost={totalServiceCost}
      totalGst={totalGst}
      grandTotal={grandTotal}
      onCopySummary={() => handleCopySummary()}
    />
  );
}
